import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

import type { Domain, Idea, Page } from "@/schemas";
import { Timestamp } from "firebase/firestore";

export interface GroupedSearchResults {
  domain: Domain;
  pages: Page[];
  ideas: Idea[];
}

export const groupPagesByDomain = (
  domains: Domain[],
  pages: Page[],
  ideas: Idea[] = [],
): GroupedSearchResults[] => {
  // Create a map of normalized domain links to domain objects
  const domainMap = new Map<string, Domain>();
  const domainLinkMap = new Map<string, string>();

  // Populate domain maps with normalized domain links
  domains.forEach((domain) => {
    const normalizedDomainLink = extractDomain(domain.url).toLowerCase();
    domainMap.set(domain.id, domain);
    domainLinkMap.set(normalizedDomainLink, domain.id);
  });

  // Initialize grouped results array with all domains and empty arrays
  const groupedPages: GroupedSearchResults[] = domains.map((domain) => ({
    domain,
    pages: [],
    ideas: [],
  }));

  // Create a map for faster lookups by domain ID
  const groupedPagesMap = new Map<string, GroupedSearchResults>();
  groupedPages.forEach((group) => {
    groupedPagesMap.set(group.domain.id, group);
  });

  // Create a map for faster lookups by normalized domain link
  const normalizedDomainMap = new Map<string, string>();
  domains.forEach((domain) => {
    const normalizedLink = extractDomain(domain.url).toLowerCase();
    normalizedDomainMap.set(normalizedLink, domain.id);
  });

  // Add pages to their respective domains
  pages.forEach((page) => {
    // Draft pages (empty URL) — match directly by domainId
    if (!page.url && page.domainId) {
      const group = groupedPagesMap.get(page.domainId);
      if (group) group.pages.push(page);
      return;
    }

    const normalizedPageDomain = extractDomain(page.url).toLowerCase();
    let matched = false;

    // First try to find domain by exact match of normalized domain
    const domainId = normalizedDomainMap.get(normalizedPageDomain);

    if (domainId && groupedPagesMap.has(domainId)) {
      // Add page to the matching domain
      groupedPagesMap.get(domainId)!.pages.push(page);
      matched = true;
    } else {
      // If no exact match, try a more flexible approach by comparing normalized domains
      for (const [domainNormalized, id] of normalizedDomainMap.entries()) {
        if (
          normalizedPageDomain === domainNormalized ||
          normalizedPageDomain.includes(domainNormalized) ||
          domainNormalized.includes(normalizedPageDomain)
        ) {
          groupedPagesMap.get(id)!.pages.push(page);
          matched = true;
          break; // Stop after finding the first match
        }
      }

      // If still no match, try a more aggressive approach - match by TLD
      if (!matched) {
        // Extract TLD (e.g., example.com -> com)
        const pageTLD = normalizedPageDomain.split(".").pop();

        // Find domains with the same TLD
        for (const [domainNormalized, id] of normalizedDomainMap.entries()) {
          const domainTLD = domainNormalized.split(".").pop();

          if (pageTLD === domainTLD) {
            // If same TLD, check if the domain parts (without TLD) are similar
            const pageDomainPart = normalizedPageDomain
              .split(".")
              .slice(0, -1)
              .join(".");
            const domainPart = domainNormalized
              .split(".")
              .slice(0, -1)
              .join(".");

            if (
              pageDomainPart.includes(domainPart) ||
              domainPart.includes(pageDomainPart)
            ) {
              groupedPagesMap.get(id)!.pages.push(page);
              matched = true;
              break;
            }
          }
        }
      }
    }

    // Debug logging for unmatched pages
    if (!matched) {
      console.log("Could not match page to any domain:", page.url);
    }
  });

  // Add ideas to their respective domains by domain_id
  ideas.forEach((idea) => {
    const group = groupedPagesMap.get(idea.domain_id);
    if (group) {
      group.ideas.push(idea);
    }
  });

  return groupedPages;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getUrlHash = (url: string): string => {
  return btoa(url).replace(/[^a-zA-Z0-9]/g, "");
};

// Function to extract domain from URL
export const extractDomain = (url: string): string => {
  // Remove http:// or https:// prefix
  const formattedUrl = url.replace(/^https?:\/\//i, "");
  // Extract domain (everything before first slash or the whole string if no slash)
  const domainMatch = formattedUrl.match(/^([^/]+)/);
  return domainMatch ? domainMatch[1] : formattedUrl;
};

// Function to format URL for display
export const formatUrlForDisplay = (url: string): string => {
  // Remove http:// or https:// prefix
  const formattedUrl = url.replace(/^https?:\/\//i, "");

  // Separate path from query parameters
  const queryIndex = formattedUrl.indexOf("?");
  const hasQuery = queryIndex !== -1;
  const baseUrl = hasQuery
    ? formattedUrl.substring(0, queryIndex)
    : formattedUrl;
  const queryParams = hasQuery ? formattedUrl.substring(queryIndex) : "";

  // If URL is short enough, return as-is
  if (formattedUrl.length <= 40) {
    return formattedUrl;
  }

  // Extract domain (everything before first slash)
  const domainMatch = baseUrl.match(/^([^/]+)/);
  const domain = domainMatch ? domainMatch[1] : baseUrl;

  // If there's no path, truncate just the domain
  if (!baseUrl.includes("/")) {
    const domainDisplay =
      domain.length > 30 ? `${domain.substring(0, 20)}...` : domain;
    // Add truncated query if present
    if (hasQuery) {
      const shortQuery =
        queryParams.length > 15
          ? `${queryParams.substring(0, 12)}...`
          : queryParams;
      return `${domainDisplay}${shortQuery}`;
    }
    return domainDisplay;
  }

  // Extract path segments (without query params)
  const pathStartIndex = baseUrl.indexOf("/");
  const path = baseUrl.substring(pathStartIndex);
  const pathSegments = path.split("/").filter(Boolean);

  // Build the display string based on path structure
  let displayUrl = "";

  if (pathSegments.length === 1) {
    displayUrl = `${domain}/${pathSegments[0]}`;
  } else if (pathSegments.length === 2) {
    displayUrl = `${domain}/${pathSegments[0]}/${pathSegments[1]}`;
  } else {
    // For longer paths: show domain start + second-to-last + last segment
    const secondToLast = pathSegments[pathSegments.length - 2];
    const lastSegment = pathSegments[pathSegments.length - 1];
    const domainDisplay =
      domain.length > 15 ? `${domain.substring(0, 12)}...` : domain;
    displayUrl = `${domainDisplay}/.../${secondToLast}/${lastSegment}`;
  }

  // Add truncated query parameters if present
  if (hasQuery && queryParams.length > 0) {
    // Always show exactly 3 dots for query param truncation
    displayUrl += "?...";
  }

  return displayUrl;
};

// Function to format path part of URL (everything after domain)
export const formatUrlPath = (url: string): string => {
  // Remove http:// or https:// prefix
  const formattedUrl = url.replace(/^https?:\/\//i, "");
  // Get everything after the domain
  const domainEndIndex = formattedUrl.indexOf("/");
  if (domainEndIndex === -1) return "";

  return formattedUrl.substring(domainEndIndex);
};

// Function to check if a string is a valid URL
export const isUrl = (str: string): boolean => {
  // First trim the string to remove any whitespace
  const trimmedStr = str.trim();

  // Check if the string is empty
  if (!trimmedStr) return false;

  try {
    // Try to create a URL object with the string
    // If it has a protocol, this will validate it directly
    const url = new URL(trimmedStr);

    // Additional validation for URLs with protocol
    // Check if the hostname has a valid TLD
    return isValidHostname(url.hostname);
  } catch {
    // If the string doesn't have a protocol, try adding http:// and check again
    try {
      const url = new URL(`http://${trimmedStr}`);

      // For URLs without protocol, we need to ensure it's a valid domain
      // Extract just the domain part (before any path, query, etc.)
      const domainPart = trimmedStr.split("/")[0].split("?")[0].split("#")[0];

      // Additional validation to ensure it's a reasonable domain name
      // Must contain at least one dot and valid characters
      const domainRegex =
        /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

      // Check both the regex and the hostname validity
      return domainRegex.test(domainPart) && isValidHostname(url.hostname);
    } catch {
      return false;
    }
  }
};

// Helper function to validate hostname with TLD check
const isValidHostname = (hostname: string): boolean => {
  // Check if hostname has at least one dot (for TLD)
  if (!hostname.includes(".")) return false;

  // Split hostname into parts
  const parts = hostname.split(".");

  // Get the TLD (last part)
  const tld = parts[parts.length - 1];

  // TLD should be at least 2 characters
  if (tld.length < 2) return false;

  // Check if the TLD is all letters (common for most valid TLDs)
  // This is a simple check - could be expanded with a list of valid TLDs
  if (!/^[a-zA-Z]+$/.test(tld)) return false;

  // Check if each part of the domain has at least one character
  for (const part of parts) {
    if (part.length === 0) return false;
  }

  return true;
};

// Function to prepare a valid URL with proper protocol and trailing slash
export const prepareValidUrl = (url: string): string => {
  // First trim the string to remove any whitespace
  const trimmedUrl = url.trim();

  // Ensure protocol is present
  const withProtocol = /^https?:\/\//i.test(trimmedUrl)
    ? trimmedUrl
    : `http://${trimmedUrl}`;

  // Ensure trailing slash — canonical form for all page URLs
  return withProtocol.endsWith("/") ? withProtocol : `${withProtocol}/`;
};

// Function to normalize URL for comparison (strips protocol and lowercases)
// Used to detect duplicates regardless of http:// vs https://
export const normalizeUrlForComparison = (url: string): string => {
  // First trim the string to remove any whitespace
  const trimmedUrl = url.trim();

  // Remove http:// or https:// prefix
  const withoutProtocol = trimmedUrl.replace(/^https?:\/\//i, "");

  // Lowercase for case-insensitive comparison, then ensure exactly one trailing slash
  const normalized = withoutProtocol.toLowerCase().replace(/\/*$/, "/");

  console.log("[normalizeUrlForComparison]", {
    original: url,
    normalized: normalized,
  });

  return normalized;
};

// Helper function to ensure a value is a Firestore Timestamp
export const ensureTimestamp = (value: unknown): Timestamp | null => {
  if (!value) return null;
  if (value instanceof Timestamp) {
    return value;
  }
  // Check for Firestore-like plain object structure
  if (
    typeof value === "object" &&
    value !== null &&
    "seconds" in value &&
    "nanoseconds" in value
  ) {
    const timestampLike = value as { seconds: unknown; nanoseconds: unknown };
    if (
      typeof timestampLike.seconds === "number" &&
      typeof timestampLike.nanoseconds === "number"
    ) {
      return new Timestamp(timestampLike.seconds, timestampLike.nanoseconds);
    }
  }
  if (value instanceof Date) {
    return Timestamp.fromDate(value);
  }
  if (typeof value === "string") {
    const parsedDate = new Date(value);
    // Check if parsing was successful
    if (!isNaN(parsedDate.getTime())) {
      return Timestamp.fromDate(parsedDate);
    }
  }
  if (typeof value === "number") {
    // Assuming milliseconds
    return Timestamp.fromMillis(value);
  }
  console.warn("Failed to convert value to Timestamp:", value); // Optional: for debugging
  return null;
};

export const toMs = (d: unknown): number => {
  const ts = ensureTimestamp(d);
  return ts ? ts.toMillis() : 0;
};
